import React, {useEffect, useReducer, useRef, useMemo, useContext, createContext} from "react";
import parse from "html-react-parser";
import {
    DndContext,
    MeasuringStrategy,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensors,
    useSensor,
} from "@dnd-kit/core";
import {LayoutGroup, motion} from "framer-motion";
import {Radio} from "antd";
import {QuestionAnswerResultBlock} from "../Question";

const DndMappingContext = createContext({});

const DND_TYPES = {
    CATEGORY: {className: "js-q-dnd-category", dropStacking: true},
    NON_TABULAR: {className: "js-q-dnd-nonTabular", dropStacking: false},
    MATCH: {className: "js-q-dnd-match", dropStacking: false},
}
const SELECT_KEYS = ['Enter', ' '];
const RESET_KEYS = ['Escape', 'Tab'];
const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
export const RESULT_VIEWS = {
    MY_ANSWERS: "MY_ANSWERS",
    CORRECT: "CORRECT",
}

const ACTIONS = {
    HYDRATE_DATA: "HYDRATE_DATA",
    SET_ASSIGNMENT: "SET_ASSIGNMENT",
    SET_PREVIEW_ASSIGNMENT: "SET_PREVIEW_ASSIGNMENT",
    COMMIT_PREVIEW_ASSIGNMENT: "COMMIT_PREVIEW_ASSIGNMENT",
    UPDATE_DRAG_STATE: "UPDATE_DRAG_STATE",
    UPDATE_ANNOUNCEMENT: "UPDATE_ANNOUNCEMENT",
    HYDRATE_RESULT_DATA: "HYDRATE_RESULT_DATA",
    UPDATE_RESULTS_VIEW: "UPDATE_RESULTS_VIEW",
    SET_ERROR: "SET_ERROR",
}

const dndQuestionInitialState = {
    uid: null,
    dataHydrated: false,
    dndType: null,
    dragSources: {},
    dropTargets: {},
    hasUserData: false,
    assignments: null,
    previewAssignments: null,
    correctAssignments: null,
    dragState: null,
    lastActiveId: null,
    announcement: "",
    resultView: RESULT_VIEWS.MY_ANSWERS,
    error: null,
}

const replaceDescriptionAnnotations = (targetObj, activeDragId, currState, assigned = false) => {
    const {assignments, previewAssignments, dragSources} = currState;
    const text = targetObj?.description || "";

    if (!text.includes('@')) return text;

    const currentMap = previewAssignments ?? assignments ?? {};
    return text.replace(/@([\w-]+)/g, (match, id) => {
        const assignedId = currentMap[id]?.[0];
        const dragItem = dragSources[assignedId];
        const assignedText = dragItem ? dragItem.text : "[blank]";

        if (activeDragId !== null && id === targetObj.id) {
            return assigned ? dragSources[activeDragId].text : `[blank: target ${id}]`;
        }
        return assignedText;
    });
};

const updateAssignmentMap = (currentMap, dragId, targetId, dropStacking, overItemId) => {
    const newMap = {...currentMap};

    // Remove from everywhere
    Object.keys(newMap).forEach(id => {
        newMap[id] = newMap[id].filter(sId => sId !== dragId);
    });

    const targetKey = targetId ?? 'pool';
    if (!newMap[targetKey]) newMap[targetKey] = [];

    // Find insertion index
    const overIndex = overItemId ? newMap[targetKey].indexOf(overItemId) : -1;

    if (overIndex !== -1) {
        // Insert at the specific spot
        newMap[targetKey].splice(overIndex, 0, dragId);
    } else {
        // Just push to the end
        newMap[targetKey].push(dragId);
    }

    // Handle single-slot logic (eject old item if target is non-stacking)
    if (targetKey !== 'pool' && !dropStacking && newMap[targetKey].length > 1) {
        const ejectedId = newMap[targetKey].find(id => id !== dragId);
        newMap[targetKey] = [dragId];
        // Send ejected item back to pool
        newMap['pool'] = [...newMap['pool'], ejectedId];
    }

    return newMap;
};

function dndQuestionReducer(state, action) {
    if (action.type === ACTIONS.HYDRATE_DATA) {
        return {...state, ...dndQuestionInitialState, ...action.payload, dataHydrated: true};
    } else if (action.type === ACTIONS.SET_ASSIGNMENT || action.type === ACTIONS.COMMIT_PREVIEW_ASSIGNMENT) {
        const {dragId, targetId, overItemId} = action.payload || {};
        const targetKey = targetId ?? 'pool';

        const currentContainer = Object.keys(state.assignments || {}).find(k =>
            state.assignments[k]?.includes(dragId)
        );
        const isSameLocation = !overItemId && currentContainer === targetKey;

        return {
            ...state,
            assignments: isSameLocation
                ? state.assignments
                : updateAssignmentMap(
                    state.assignments,
                    dragId,
                    targetKey,
                    state.dndType.dropStacking,
                    overItemId
                ),
            previewAssignments: dndQuestionInitialState.previewAssignments,
            dragState: dndQuestionInitialState.dragState,
            lastActiveId: dragId ?? state.dragState?.activeId,
            hasUserData: true,
        };
    } else if (action.type === ACTIONS.UPDATE_DRAG_STATE) {
        const newDragState = action.payload
            ? {...state.dragState, ...action.payload}
            : dndQuestionInitialState.dragState;
        const newLastActiveId = action.payload?.activeId
            ?? state.dragState?.activeId
            ?? state.lastActiveId;
        return {
            ...state,
            previewAssignments: (action.payload && action.payload.isKeyboardMove && !state.previewAssignments)
                ? {...state.assignments}
                : state.previewAssignments,
            dragState: newDragState,
            lastActiveId: newLastActiveId,
        };
    } else if (action.type === ACTIONS.SET_PREVIEW_ASSIGNMENT) {
        if (!action.payload) {
            return {
                ...state,
                previewAssignments: dndQuestionInitialState.assignments,
                dragState: dndQuestionInitialState.dragState,
            };
        }
        return {
            ...state,
            previewAssignments: updateAssignmentMap(
                state.assignments,
                action.payload.dragId,
                action.payload.targetId,
                state.dndType.dropStacking
            ),
            dragState: {...state.dragState, overId: action.payload.targetId}
        };
    } else if (action.type === ACTIONS.HYDRATE_RESULT_DATA) {
        return {...state, ...action.payload};
    } else if (action.type === ACTIONS.UPDATE_RESULTS_VIEW) {
        const nextView = action.payload;
        return {
            ...state,
            resultView: nextView,
            previewAssignments: nextView === RESULT_VIEWS.MY_ANSWERS
                ? dndQuestionInitialState.previewAssignments
                : state.correctAssignments
        }
    } else if (action.type === ACTIONS.UPDATE_ANNOUNCEMENT) {
        const newMessage = action.payload === state.announcement ? action.payload + "\u200B" : action.payload;
        // console.log("announcement:", newMessage);
        return {...state, announcement: newMessage};
    } else if (action.type === ACTIONS.SET_ERROR) {
        return {...state, error: action.payload, dataHydrated: true};
    } else {
        return state;
    }
}

const DragAndDropQuestion = props => {
    const {question, result, resultsView = RESULT_VIEWS.MY_ANSWERS, displayAnswerOnResult, readOnly, onLoad, onError, updateQuestionData, inputValues} = props;
    const [state, dispatch] = useReducer(dndQuestionReducer, dndQuestionInitialState);
    const instructionsHtmlRef = useRef();
    const dndHtmlRef = useRef();
    const questionRef = useRef();

    useEffect(() => {
        // console.log("state:", state);
    }, [state]);

    useEffect(() => {
        onError?.(state.error);
    }, [state.error]);

    useEffect(() => {
        handleUpdateResultsView(resultsView);
    }, [resultsView]);

    useEffect(() => {
        let hasError = false;
        const html = question?.formattedInstructions || question?.instructions || '';
        if (!html || html === "No question content") return;

        const parser = new DOMParser();
        const doc= parser.parseFromString(html, 'text/html');
        const dndTypeEntry = Object.entries(DND_TYPES).find(([_, config]) =>
            doc.querySelector(`.${config.className}`)
        );

        if (!dndTypeEntry) {
            hasError = true;
            dispatch({type: ACTIONS.SET_ERROR, payload: "No drag and drop type found."});
            return;
        }

        const dndType = dndTypeEntry[1];
        let newDragSources = {};
        Array.from(doc.querySelectorAll('.js-drag-obj')).map(obj => {
            const id = String(obj.dataset.dragObjectIndex);
            const text = obj.innerText.trim();
            newDragSources[id] = {id, text};
        });
        const newDropTargets = {
            'pool': {id: 'pool', description: "Selection pool"}
        };
        Array.from(doc.querySelectorAll('.js-drag-area')).map(obj => {
            const id = String(obj.dataset.dragAreaIndex);
            let description = "";
            if (dndType.className === DND_TYPES.NON_TABULAR.className) {
                const fullHTML = obj.parentElement.innerHTML;
                const dropKeyString = `data-drag-area-index="${id}"`;
                const dropKeyIndex = fullHTML.indexOf(dropKeyString);
                if (dropKeyIndex !== -1) {
                    let start = fullHTML.lastIndexOf(".", dropKeyIndex);
                    start = (start === -1) ? 0 : start + 1;
                    let end = fullHTML.indexOf(".", dropKeyIndex);
                    end = (end === -1) ? fullHTML.length : end + 1;
                    const rawSentence = fullHTML.substring(start, end);
                    const annotatedSentence = rawSentence.replace(
                        /<[^>]*data-drag-area-index="(\d+)"[^>]*>.*?<\/[^>]*>/g,
                        (match, tokenId) => `@${tokenId}`
                    );
                    description = annotatedSentence
                        .replace(/<[^>]*>/g, '')
                        .trim();
                }
            } else if (dndType.className === DND_TYPES.CATEGORY.className) {
                description = obj.textContent.trim();
            } else if (dndType.className === DND_TYPES.MATCH.className) {
                const sibling = obj.nextElementSibling;
                description = sibling ? sibling.textContent.trim() : "";
            }
            newDropTargets[id] = {id, description};
        });

        const poolRegex = /<div[^>]*class="[^"]*drag-obj-blk[^"]*"[^>]*>[\s\S]*?<\/div>/;
        const parts = html.split(poolRegex);

        instructionsHtmlRef.current = parts[0] || "";
        dndHtmlRef.current = parts[1] || "";

        if (Object.keys(newDragSources).length === 0 || Object.keys(newDropTargets).length === 0) {
            hasError = true;
            dispatch({type: ACTIONS.SET_ERROR, payload: "Question content error, unable to parse question."});
            return;
        }

        const assignmentValues = result?.userInput ?? inputValues ?? "{}";
        const initInputValues = parseAssignmentString(assignmentValues) ?? {};
        const newAssignments = {
            'pool': Object.values(newDragSources).map(dragObj => dragObj.id),
        };
        Object.values(newDropTargets).forEach(dropObj => {
            if (dropObj.id !== 'pool') {
                const dropTargets = initInputValues[dropObj.id] || [];
                newAssignments[dropObj.id] = dropTargets;
                newAssignments['pool'] = newAssignments['pool'].filter(
                    id => !dropTargets.includes(id)
                );
            }
        });

        if (hasError) return;
        const uid = `${question.questionId}-${Math.random().toString(36).substr(2, 9)}`;

        dispatch({
            type: ACTIONS.HYDRATE_DATA,
            payload: {
                uid: uid,
                dragSources: newDragSources,
                dropTargets: newDropTargets,
                dndType,
                assignments: newAssignments,
            }
        });
    }, [question]);

    useEffect(() => {
        if (state.dataHydrated && questionRef.current) {
            if (window.MathJax?.typesetPromise) {
                window.MathJax.typesetPromise([questionRef.current])
                    .catch(() => dispatch({type: ACTIONS.SET_ERROR, payload: "MathJax failed to load."}))
                    .finally(() => handleInitialFocus());
            } else {
                handleInitialFocus();
            }
        }

        function handleInitialFocus() {
            onLoad?.(state.inputs);
            if (readOnly) return;

            setTimeout(() => {
                questionRef.current?.focus({preventScroll: true});
            }, 50);
        }
    }, [state.dataHydrated, readOnly]);

    useEffect(() => {
        if (!state.dataHydrated || !state.assignments || readOnly || result) return;

        const rawData = Object.entries(state.dropTargets)
            .filter(([id]) => id !== 'pool')
            .map(([dropId]) => {
                const assignedIds = state.assignments[dropId] || [];
                return {
                    name: `${question.questionId}[0]`,
                    value: assignedIds.map(dragId => `${dragId}=${dropId}`).join(',')
                };
            });

        const totalItems = Object.keys(state.dragSources).length;
        const itemsInPool = state.assignments?.['pool']?.length || 0;
        const isSubmittable = totalItems > 0 && itemsInPool === 0;

        updateQuestionData?.(rawData, isSubmittable);
    }, [state.dataHydrated, state.assignments, readOnly, result]);

    useEffect(() => {
        if (!result || !state.dataHydrated || state.correctAssignments !== null) return;

        const correctInputMap = parseAssignmentString(question.correctAnswer || "");
        const enrichedSources = {...state.dragSources};
        Object.entries(correctInputMap).forEach(([dropId, dragIds]) => {
            dragIds.forEach(dragId => {
                if (enrichedSources[dragId]) {
                    enrichedSources[dragId].correctDropId = dropId;
                }
            });
        });
        const userAssignments = parseAssignmentString(result.userInput ?? "");

        dispatch({type: ACTIONS.HYDRATE_RESULT_DATA, payload: {
                dragSources: enrichedSources,
                correctAssignments: correctInputMap,
                ...(!state.hasUserData && {assignments: userAssignments}),
            }
        });
    }, [question, result, state.dataHydrated, state.hasUserData, state.dragSources, state.assignments, state.correctAssignments]);

    useEffect(() => {
        if (!state.dataHydrated || !questionRef.current) return;

        const handle = requestAnimationFrame(() => {
            if (window.MathJax?.typesetPromise) {
                window.MathJax.typesetPromise([questionRef.current]);
            }
        });

        return () => cancelAnimationFrame(handle);
    }, [state.assignments]);

    const parseAssignmentString = string => {
        if (!string) return {};

        const assignments = {};
        string.replace(/[{}]/g, '').split(',').forEach(pair => {
            const [dragId, dropId] = pair.split('=').map(s => s?.trim());
            if (dragId && dropId) {
                (assignments[dropId] ??= []).push(dragId);
            }
        });
        return assignments;
    };

    const buildDragState = (dragId, eventType, assignments) => {
        const fromId = Object.keys(assignments).find(key => assignments[key].includes(dragId));
        return {
            activeId: dragId,
            isKeyboardMove: eventType === 'keydown',
            fromId: fromId,
            overId: fromId,
        }
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 2,
            },
        })
    );

    const content = useMemo(() => {
        if (!state.dataHydrated) return;

        const parseOptions = {
            replace: (domNode) => {
                if (domNode.attribs?.class?.includes('js-drag-area')) {
                    const dropId = String(domNode.attribs['data-drag-area-index']);
                    return (
                        <DroppableArea
                            id={dropId}
                            questionId={question.questionId}
                            Tag={domNode.name}
                            readOnly={readOnly}
                            dndType={state.dndType}
                        />
                    );
                }
            }
        };
        return parse(dndHtmlRef.current ?? '', parseOptions);
    }, [dndHtmlRef.current, question, state.dataHydrated, state.dndType, readOnly]);

    const handleDragStart = props => {
        const {activatorEvent, active} = props;
        if (!active) return;
        const dragId = active?.data.current.id;
        if (!dragId) return;
        updateDragStart(dragId, activatorEvent.type, state);
    }

    const updateDragStart = (dragId, evenType, currState) => {
        const dragObj = currState.dragSources[dragId];
        if (!dragObj) return;

        const newDragState = buildDragState(dragObj.id, evenType, currState.assignments);
        dispatch({type: ACTIONS.UPDATE_DRAG_STATE, payload: newDragState});
        dispatch({type: ACTIONS.UPDATE_ANNOUNCEMENT, payload: `Picked up ${dragObj.text}.`});
    }

    const handleDragOver = event => {
        const {over} = event;
        const targetId = over?.data.current.id ?? 'pool';
        updateDragOver(targetId, state);
    }

    const updateDragOver = (overId, currState) => {
        const activeId = currState.dragState?.activeId;
        if (!activeId) return;

        const targetId = overId ?? "pool";
        if (currState.dragState.overId === targetId) return;

        const targetObj = currState.dropTargets[targetId];
        let msg = targetId === 'pool'
            ? "Back over selection pool."
            : `Over target ${targetId}: ${replaceDescriptionAnnotations(targetObj, activeId, currState)}`;

        dispatch({type: ACTIONS.UPDATE_DRAG_STATE, payload: {overId: targetId}});
        setTimeout(() => {
            dispatch({type: ACTIONS.UPDATE_ANNOUNCEMENT, payload: msg});
        }, 10);
    }

    const handleDragEnd = event => {
        const {over} = event;
        const targetId = over?.data.current.id ?? 'pool';
        updateDragEnd(targetId, state);
    };

    const updateDragEnd = (overId, currState) => {
        const activeId = currState.dragState?.activeId;
        const dragObj = currState.dragSources[activeId];

        if (!dragObj) return;

        const targetObj = currState.dropTargets[overId];
        const overItemId = currState.dragSources[overId] ? overId : null;
        const targetId = targetObj
            ? targetObj.id
            : (overItemId
                ? Object.keys(currState.assignments).find(key => currState.assignments[key].includes(overItemId))
                : 'pool');

        const returnCheck = targetId === currState.dragState.fromId;

        let msg= '';
        if (returnCheck) {
            msg = `${dragObj.text} returned.`
            dispatch({type: ACTIONS.UPDATE_DRAG_STATE, payload: null});
        } else {
            if (targetId === 'pool') msg = `${dragObj.text} returned to ${currState.dropTargets['pool'].description}.`;
            else msg = `${dragObj?.text} assigned to target ${targetObj.id}. ${replaceDescriptionAnnotations(targetObj, dragObj.id, currState, true)}`
            dispatch({
                type: ACTIONS.SET_ASSIGNMENT,
                payload: {targetId: targetId, dragId: activeId, overItemId: overItemId}
            });
        }
        dispatch({type: ACTIONS.UPDATE_ANNOUNCEMENT, payload: msg});
    }

    const handleDragCancel = () => {
        const msg = `${state.dragState?.isKeyboardMove ? 'Move' : 'Drag'} canceled.`;
        dispatch({type: ACTIONS.UPDATE_ANNOUNCEMENT, payload: msg});
        dispatch({type: ACTIONS.UPDATE_DRAG_STATE, payload: null});
    }

    const handleUpdateResultsView = view => {
        dispatch({type: ACTIONS.UPDATE_RESULTS_VIEW, payload: view})
        dispatch({
            type: ACTIONS.UPDATE_ANNOUNCEMENT,
            payload: `Showing ${view === RESULT_VIEWS.MY_ANSWERS ? 'your answers' : 'correct answers'}.`
        });
    }

    if (state.error) return <></>

    return (
        <div
            ref={questionRef}
            tabIndex={-1}
            className="ch-q-dnd"
            role="application"
            aria-label="Drag and drop question"
        >
            <DndMappingContext.Provider value={{
                uid: state.uid,
                dragSources: state.dragSources,
                dropTargets: state.dropTargets,
                assignments: state.assignments,
                previewAssignments: state.previewAssignments,
                dragState: state.dragState,
                lastActiveId: state.lastActiveId,
                updateDragStart,
                updateDragOver,
                updateDragEnd,
                handleDragCancel,
                result: result,
                displayAnswerOnResult: displayAnswerOnResult,
                readOnly: readOnly,
                dispatch: dispatch,
            }}>
                <LayoutGroup id={state.uid}>
                    <DndContext
                        sensors={sensors}
                        autoScroll={false}
                        onDragStart={readOnly ? undefined : handleDragStart}
                        onDragOver={readOnly ? undefined : handleDragOver}
                        onDragEnd={readOnly ? undefined : handleDragEnd}
                        onDragCancel={readOnly ? undefined : handleDragCancel}
                        measuring={{
                            droppable: {
                                strategy: MeasuringStrategy.Always,
                            },
                        }}
                        accessibility={{
                            screenReaderInstructions: {draggable: ""},
                            announcements: {
                                onDragStart() {},
                                onDragMove() {},
                                onDragOver() {},
                                onDragEnd() {},
                            }
                        }}
                    >
                        {!readOnly && (
                            <>
                                <div id="dnd-instructions" className="ch-sr-only">
                                    To move an item either drag an item to a target or, press Enter or Space to select it. Then use the arrow keys to cycle through targets, and Enter or Space again to drop. Press Escape to cancel.
                                </div>
                                <div className="ch-sr-only" aria-live="assertive" aria-atomic="true">
                                    {state.announcement}
                                </div>
                            </>
                        )}
                        {instructionsHtmlRef.current && (
                            <div dangerouslySetInnerHTML={{__html: instructionsHtmlRef.current}} />
                        )}
                        <div
                            className="ch-q-dnd-dragPool"
                            aria-label="Draggables selection pool"
                            role="listbox"
                        >
                            {Object.values(state.dragSources).map((dragObj) => {
                                const assignemnts = state.previewAssignments ?? state.assignments ?? {};
                                const inPool = assignemnts['pool']?.includes(dragObj.id) ?? false;
                                return (
                                    <div
                                        key={`drag-obj-wrap-${dragObj.id}`}
                                        className="ch-q-dnd-drag-wrap"
                                    >
                                        <div
                                            className={`ch-q-dnd-drag-holder ${!inPool && "ch-q-dnd-drag-holder-a"}`}
                                            aria-hidden={true}
                                        >
                                            {dragObj.text}
                                        </div>
                                        {inPool && (
                                            <DraggableItem
                                                key={`drag-item-${dragObj.id}`}
                                                id={dragObj.id}
                                                questionId={question.questionId}
                                                dropId={'pool'}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {content}
                        {result && displayAnswerOnResult && (
                            <QuestionAnswerResultBlock
                                result={result}
                                questionAnswerType={question.questionAnswerType}
                                dndValue={state.resultView}
                                dndOnChange={handleUpdateResultsView}
                            />
                        )}
                    </DndContext>
                </LayoutGroup>
            </DndMappingContext.Provider>
        </div>
    )
}

const DroppableArea = props => {
    const {id, questionId, Tag, dndType} = props;
    const {uid, dropTargets, dragSources, assignments, previewAssignments, dragState} = useContext(DndMappingContext);
    const {isOver, setNodeRef} = useDroppable({
        id: `${uid}-target-${id}`,
        data: {id: id},
    });
    const dropObj = dropTargets[id];
    if (!dropObj) return;

    const isActive = isOver || (dragState?.overId === id);
    const dropDescription = replaceDescriptionAnnotations(dropObj, null, {assignments, previewAssignments, dragSources});

    const itemRef = useRef();

    const dropAreaAssignments = useMemo(() => {
        const currentMap = previewAssignments ?? assignments ?? {};
        return currentMap[id] || [];
    }, [assignments, previewAssignments, id]);

    return (
        <Tag
            ref={(node) => {
                setNodeRef(node);
                itemRef.current = node;
            }}
            className={[
                "ch-q-dnd-drop",
                isActive && "ch-q-dnd-drop-over",
                (dropAreaAssignments.length > 0 && dragState?.fromId !== id) && "ch-q-dnd-drop-fill"
            ].filter(Boolean).join(" ")}
            aria-label={dropDescription || `Target ${id}`}
        >
            {dropDescription && dndType.className === DND_TYPES.CATEGORY.className && (
                <span className="ch-q-dnd-drop-label">
                    {dropDescription}
                </span>
            )}
            {dropAreaAssignments.map(dragId => {
                return (
                    <DraggableItem
                        key={`drag-item-${dragId}`}
                        id={dragId}
                        dropId={id}
                        questionId={questionId}
                    />
                )
            })}
        </Tag>
    );
}

const DraggableItem = props => {
    const {id, dropId, questionId} = props;
    const {
        uid,
        dragSources,
        dropTargets,
        assignments,
        previewAssignments,
        dragState,
        lastActiveId,
        updateDragStart,
        updateDragOver,
        updateDragEnd,
        handleDragCancel,
        result,
        readOnly,
        dispatch,
    } = useContext(DndMappingContext);
    const isDisabled = (readOnly || result);
    const {listeners, setNodeRef, isDragging} = useDraggable({
        id: `${uid}-drag-${id}`,
        disabled: isDisabled,
        data: {id: id},
    });
    const dragObj = dragSources[id];
    const isActive = (isDragging || dragState?.activeId === id);
    const showValidation = result && dropId !== 'pool';
    const isCorrect = showValidation ? dragObj?.correctDropId === dropId : null;

    const itemRef = useRef();

    useEffect(() => {
        if (isDisabled) return;

        if (lastActiveId === id && itemRef.current) {
            const rafId = requestAnimationFrame(() => {
                if (itemRef.current && document.activeElement !== itemRef.current) {
                    itemRef.current.focus({preventScroll: true});
                }
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [lastActiveId, id, isDisabled]);

    const handleKeyDown = (event) => {
        if (SELECT_KEYS.includes(event.key)) {
            event.preventDefault();
            if (dragState && dragState.activeId === id) {
                updateDragEnd(dragState.overId, {dragState, dragSources, dropTargets, assignments});
            } else if (!dragState) {
                updateDragStart(id, 'keydown', {assignments, dragSources});
            }
        } else if (ARROW_KEYS.includes(event.key)) {
            if (!dragState || dragState.activeId !== id || !dragState.isKeyboardMove) return;
            event.preventDefault();

            const targetIds = Object.keys(dropTargets);
            const currentOverId = dragState.overId ?? "pool";
            const currentIndex = targetIds.indexOf(currentOverId);

            let nextIndex;
            if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
                nextIndex = (currentIndex + 1) % targetIds.length;
            } else {
                nextIndex = (currentIndex - 1 + targetIds.length) % targetIds.length;
            }

            const nextTargetId = targetIds[nextIndex];

            dispatch({
                type: ACTIONS.SET_PREVIEW_ASSIGNMENT,
                payload: { targetId: nextTargetId, dragId: id }
            });

            updateDragOver(nextTargetId, {dragSources, dropTargets, dragState, assignments, previewAssignments});
        } else if (RESET_KEYS.includes(event.key)) {
            if (dragState?.activeId === id) {
                handleDragCancel();
            }
        }
    };

    const handleClick = () => {
        if (isDisabled || dropId === 'pool') return;

        const mockDragState = {
            activeId: id,
            fromId: dropId,
            isKeyboardMove: false,
        }
        updateDragEnd(null, {dragState: mockDragState, dragSources, dropTargets, assignments});
    }

    return (
        <motion.span
            id={`${questionId}-drag-${id}`}
            drag={!isDisabled}
            dragMomentum={false}
            dragSnapToOrigin={true}
            layoutId={`${uid}-${id}`}
            layout="position"
            transition={{
                type: "tween",
                ease: "linear",
                duration: 0.15
            }}
            {...(!isDisabled && {
                ...listeners,
                tabIndex: 0,
                onKeyDown: handleKeyDown,
                onClick: handleClick,
            })}
            ref={(node) => {
                setNodeRef(node);
                itemRef.current = node;
            }}
            className={[
                "ch-q-dnd-drag",
                isActive && "ch-q-dnd-drag-dragging",
                isDisabled && "ch-q-dnd-drag-RO",
                (isCorrect === true) && "ch-q-dnd-drag-correct",
                (isCorrect === false) && "ch-q-dnd-drag-incorrect",
            ].filter(Boolean).join(" ")}
            aria-roledescription="draggable item"
        >
            {dragObj.text}
            {isCorrect !== null && (
                <span className="ch-sr-only">
                    {isCorrect ? ", Correct Answer" : `, Incorrect. Correct answer was target ${dragObj.correctDropId}`}
                </span>
            )}
        </motion.span>
    );
};

export const DragAndDropQuestionResultsSwitch = props => {
    const {value, onChange} = props;

    return (
        <Radio.Group
            className="ch-q-dnd-results-switch margin-t--m"
            buttonStyle="solid"
            optionType="button"
            value={value}
            options={[
                {value: RESULT_VIEWS.MY_ANSWERS, label: "My answers"},
                {value: RESULT_VIEWS.CORRECT, label: "Correct answers"},
            ]}
            onChange={onChange}
        />
    );
};

export default DragAndDropQuestion;